import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DigitOnlyModule } from '@uiowa/digit-only';
import { NgxMaskModule } from 'ngx-mask';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { AddressInputComponent } from './components/address-input/address-input.component';
import { AlertBoxComponent } from './components/alert-box/alert-box.component';
import { BoxLinkComponent } from './components/box-link/box-link.component';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { ButtonComponent } from './components/button/button.component';
import { CardLinkComponent } from './components/card-link/card-link.component';
import { CheckboxInputComponent } from './components/checkbox-input/checkbox-input.component';
import { CheckboxListInputComponent } from './components/checkbox-list-input/checkbox-list-input.component';
import { CollapsibleComponent } from './components/collapsible/collapsible.component';
import { ColorPanelComponent } from './components/color-panel/color-panel.component';
import { DatepickerInputComponent } from './components/datepicker-input/datepicker-input.component';
import { DividerComponent } from './components/divider/divider.component';
import { DropdownInputComponent } from './components/dropdown-input/dropdown-input.component';
import { DropdownSelectorComponent } from './components/dropdown-selector/dropdown-selector.component';
import { ErrorComponent } from './components/error/error.component';
import { FaqItemComponent } from './components/faq-item/faq-item.component';
import { FaqSurveyComponent } from './components/faq-survey/faq-survey.component';
import { FileInputComponent } from './components/file-input/file-input.component';
import { FooterComponent } from './components/footer/footer.component';
import { FormVisualizerComponent } from './components/form-visualizer/form-visualizer.component';
import { HeaderComponent } from './components/header/header.component';
import { IconWithTextComponent } from './components/icon-with-text/icon-with-text.component';
import { InputLabelComponent } from './components/input-label/input-label.component';
import { LoadingPlaceholderComponent } from './components/loading-placeholder/loading-placeholder.component';
import { LoadingSpinnerComponent } from './components/loading-spinner/loading-spinner.component';
import { MenuComponent } from './components/menu/menu.component';
import { MenuSelectorComponent } from './components/menu-selector/menu-selector.component';
import { ModalComponent } from './components/modal/modal.component';
import { NavbarComponent } from './components/navbar/navbar.component';
import { NumericInputComponent } from './components/numeric-input/numeric-input.component';
import { QuickAccessLinkComponent } from './components/quick-access-link/quick-access-link.component';
import { RadioInputComponent } from './components/radio-input/radio-input.component';
import { RatingInputComponent } from './components/rating-input/rating-input.component';
import { ReadOnlyInputComponent } from './components/read-only-input/read-only-input.component';
import { SearchInputComponent } from './components/search-input/search-input.component';
import { SmallCardLinkComponent } from './components/small-card-link/small-card-link.component';
import { StepperComponent } from './components/stepper/stepper.component';
import { StepsHeaderComponent } from './components/steps-header/steps-header.component';
import { TableComponent } from './components/table/table.component';
import { TagSelectorComponent } from './components/tag-selector/tag-selector.component';
import { TextAreaInputComponent } from './components/text-area-input/text-area-input.component';
import { TextInputComponent } from './components/text-input/text-input.component';
import { TextListInputComponent } from './components/text-list-input/text-list-input.component';
import { TextWithLinkComponent } from './components/text-with-link/text-with-link.component';
import { UserMenuComponent } from './components/user-menu/user-menu.component';
import { AutofocusDirective } from './directives/autofocus.directive';
import { SafePipe } from './pipes/safe.pipe';
import { RelatedServicesComponent } from './related-services/related-services.component';
import { CarouselComponent } from './components/carousel/carousel.component';
import { PasswordInputComponent } from './components/password-input/password-input.component';
import { DatalistInputComponent } from './components/datalist-input/datalist-input.component';
import { ZipCodePipe } from './pipes/zipcode.pipe';
import { CurrencyFormatPipe } from './pipes/currency-format.pipe';
import { CurrencyMaskModule } from 'ng2-currency-mask';
import { CurrencyInputComponent } from './components/currency-input/currency-input.component';
import { FiltrosListaComponent } from './components/filtros-lista/filtros-lista.component';
import { HtmlOutputComponent } from './components/html-output/html-output.component';
import { ModalTableDescriptionComponent } from './components/table/modal-table-description/modal-table-description.component';
import { UsageTermsComponent } from './components/usage-terms/usage-terms.component';
import { FooterSocialMediasComponent } from './components/footer-social-medias/footer-social-medias.component';
import { ServiceCollapsibleComponent } from './components/service-collapsible/service-collapsible.component';
import { LucideIconComponent } from './components/lucide-icon/lucide-icon.component';
import { LucideAngularModule, icons } from 'lucide-angular';


const MODULES = [
  CommonModule,
  DigitOnlyModule,
  FormsModule,
  NgxMaskModule,
  NgxTippyModule,
  ReactiveFormsModule,
  RouterModule,
  CurrencyMaskModule,
  FooterSocialMediasComponent
];

const COMPONENTS = [
  AddressInputComponent,
  AlertBoxComponent,
  BoxLinkComponent,
  BreadcrumbComponent,
  ButtonComponent,
  CardLinkComponent,
  CheckboxInputComponent,
  CheckboxListInputComponent,
  CollapsibleComponent,
  ColorPanelComponent,
  DatepickerInputComponent,
  DividerComponent,
  DropdownInputComponent,
  DropdownSelectorComponent,
  ErrorComponent,
  FaqItemComponent,
  FaqSurveyComponent,
  FileInputComponent,
  FooterComponent,
  FormVisualizerComponent,
  HeaderComponent,
  IconWithTextComponent,
  InputLabelComponent,
  LoadingPlaceholderComponent,
  LoadingSpinnerComponent,
  MenuComponent,
  MenuSelectorComponent,
  ModalComponent,
  NavbarComponent,
  NumericInputComponent,
  QuickAccessLinkComponent,
  RadioInputComponent,
  RatingInputComponent,
  ReadOnlyInputComponent,
  SearchInputComponent,
  SmallCardLinkComponent,
  StepperComponent,
  StepsHeaderComponent,
  TableComponent,
  TagSelectorComponent,
  TextAreaInputComponent,
  TextInputComponent,
  TextListInputComponent,
  TextWithLinkComponent,
  UserMenuComponent,
  RelatedServicesComponent,
  CarouselComponent,
  PasswordInputComponent,
  DatalistInputComponent,
  CurrencyInputComponent,
  FiltrosListaComponent,
  HtmlOutputComponent,
  ModalTableDescriptionComponent,
  UsageTermsComponent,
  ServiceCollapsibleComponent,
  LucideIconComponent
];

const DIRECTIVES = [AutofocusDirective];

const PIPES = [SafePipe, CurrencyFormatPipe, ZipCodePipe];

@NgModule({
  declarations: [...COMPONENTS, ...DIRECTIVES, ...PIPES],
  imports: [...MODULES,   
    LucideAngularModule.pick(icons)],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  exports: [...MODULES, ...COMPONENTS, ...DIRECTIVES, ...PIPES]
})
export class SharedModule {}
